import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import useGetSubscription from './useGetSubscription';
import { createInvoiceHoistedUrl } from '../../services/subscriptions';
import useUser from '../global/useUser';
import { LoadedUser } from '../../interfaces';
import { openExternalUrl } from '../../utils/global/helper';

interface InvoiceHoistedUrlOptions {
  autoOpen?: boolean;
  onError?: () => void | Promise<void>;
}

function useInvoiceHoistedUrl({
  autoOpen = true,
  onError,
}: InvoiceHoistedUrlOptions = {}) {
  const queryClient = useQueryClient();
  const { user } = useUser<LoadedUser>();
  const { data, isFetching } = useGetSubscription();

  const businessId = user?.business?.id;
  const { data: cachedInvoices } = useQuery<Map<string, string>>({
    queryKey: ['cached-invoices', data?.subscription?.id, businessId],
    queryFn: () => new Map(),
  });

  const launchInvoice = useMutation({
    mutationFn: async (invoiceId: string) => {
      if (!data?.subscription || isFetching) return null;

      const cachedValue = cachedInvoices?.get(invoiceId);
      if (cachedValue) return cachedValue;

      const response = await createInvoiceHoistedUrl({
        subscriptionId: data.subscription.id,
        businessId,
        invoiceId,
      });

      return response?.url;
    },
    onSuccess: (url, invoiceId) => {
      if (url) {
        if (autoOpen) {
          openExternalUrl(url);
        }

        queryClient.setQueryData(
          ['cached-invoices', data?.subscription?.id, businessId],
          (prev: typeof cachedInvoices) => {
            const newMap = new Map(prev);
            newMap.set(invoiceId, url);
            return newMap;
          },
        );
      }
    },
    onError,
  });

  const checkIsInvoicePending = (invoiceId: string) =>
    launchInvoice.isPending && launchInvoice.variables === invoiceId;

  const checkIsInvoiceSuccess = (invoiceId: string) =>
    launchInvoice.isSuccess && launchInvoice.variables === invoiceId;

  return {
    launchInvoice,
    checkIsInvoicePending,
    checkIsInvoiceSuccess,
  };
}

export default useInvoiceHoistedUrl;
